import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { KOMA_COUNT } from '~/constants/koma'
import { slotTimeLabel } from '~/pages/SpotBidding/models/komaOptions'

type Props = {
  /** 選択中の開始コマ (1〜48)。 未選択・不正なら全セル非選択で描画する。 */
  timeCodeFrom: number | undefined
  /** 選択中の終了コマ (1〜48)。 */
  timeCodeTo: number | undefined
}

/**
 * ブロック作成モーダルのコマ範囲入力に添える 48 セルのミニバー
 * (spec `spot-bidding.md` 機能詳細「コマ範囲: 開始〜終了のセレクタで指定し、
 * 48セルのミニバーで選択範囲を視覚化する。 選択中のコマ数と時間数
 * （「Nコマ / M時間」）を表示する」、 AC-BSP-030)。
 *
 * design HTML (`design/bidding/spot.html`) のブロック作成モーダル「時間帯」欄
 * `paintSlotBar()` (L6087-6115) が同じ 48 セル範囲プレビューの正本実装を持つ。
 * 選択色は同関数のコメント (L6098-6101) の通り、以前の `--brand-blue`
 * (dark でも固定 RGB) から `--border-accent-primary` へ変更済み
 * (dark mode で `--surface-tertiary` とのコントラスト比が 1.01:1 まで落ち、
 * 選択/未選択が判別できなくなっていたため。 WCAG 1.4.11 の非テキスト
 * コントラスト対応)。 本 component はその正本の配色 (選択中 = `Border.accentPrimary`、
 * 非選択 = `Surface.tertiary`) をそのまま踏襲し、 「ミニ」 (=一覧ヘッダーに
 * 収まる高さ) にするため状態別の配色・クリック操作は持たない読み取り専用の
 * 範囲プレビューに留める。
 *
 * `open`/`gc-closed` 等のコマ状態は持たない (= 入力段階でまだ受付状態と
 * 無関係) ため [[KomaRibbon]] をそのまま流用せず専用の軽量 component にする。
 */
export const TimeCodeRangeMiniBar = ({
  timeCodeFrom,
  timeCodeTo,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const hasRange =
    timeCodeFrom !== undefined &&
    timeCodeTo !== undefined &&
    Number.isFinite(timeCodeFrom) &&
    Number.isFinite(timeCodeTo) &&
    timeCodeTo >= timeCodeFrom
  const slotCount = hasRange ? timeCodeTo - timeCodeFrom + 1 : 0
  // 30 分 (0.5h) × コマ数。 小数第1位までで丸め、 ".0" は落として整数時間は
  // "2時間" のように出す (spec の表記「Nコマ / M時間」に合わせる)。
  const hoursLabel =
    slotCount > 0 ? String(Math.round(slotCount * 0.5 * 10) / 10) : '0'

  return (
    <Box width="100%">
      <Flex
        gap="1px"
        width="100%"
        aria-label={t('page:biddingBlock.form.timeCodeRangeMiniBarLabel')}
        role="img"
      >
        {Array.from({ length: KOMA_COUNT }, (_, i) => i + 1).map((koma) => {
          const isSelected =
            hasRange && koma >= timeCodeFrom && koma <= timeCodeTo
          return (
            <Box
              key={koma}
              flex="1 1 0"
              height="8px"
              borderWidth="1px"
              borderColor={
                isSelected ? 'Border.accentPrimary' : 'Border.lowEmphasis'
              }
              borderRadius="sm"
              backgroundColor={
                isSelected ? 'Border.accentPrimary' : 'Surface.tertiary'
              }
              // テスト用の掴みどころ (jsdom に描画色を読む手段が無く、 role や可視文字列でも
              // 48 セルそれぞれの選択状態を判別できないため)。
              data-koma={koma}
              data-selected={isSelected}
              title={`${koma} (${slotTimeLabel(koma)})`}
            />
          )
        })}
      </Flex>
      <Text marginTop={1} color="Text.midEmphasis" fontSize="sm">
        {hasRange
          ? t('page:biddingBlock.form.timeCodeRangeSummary', {
              count: slotCount,
              hours: hoursLabel,
            })
          : t('page:biddingBlock.form.timeCodeRangeUnselected')}
      </Text>
    </Box>
  )
}
